/**
 * Parses a date-only string (YYYY-MM-DD) as local time instead of UTC.
 *
 * When JavaScript parses "2026-01-10" with `new Date()`, it interprets it as
 * UTC midnight. This causes the date to shift back one day when displayed in
 * timezones behind UTC (e.g., EST, CST, PST).
 *
 * This function parses the date components manually and creates a Date object
 * in local time, ensuring "2026-01-10" displays as January 10th regardless of timezone.
 *
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object in local time, or null if invalid
 *
 * @example
 * // API returns "2026-01-10"
 * const date = parseDateString("2026-01-10");
 * date.toLocaleDateString('en-US'); // "1/10/2026" (correct, not "1/9/2026")
 */
export function parseDateString(dateString: string | null | undefined): Date | null {
   if (!dateString) {
      return null;
   }

   const parts = dateString.split('-');
   if (parts.length !== 3) {
      return null;
   }

   const [yearStr, monthStr, dayStr] = parts;
   const year = parseInt(yearStr, 10);
   const month = parseInt(monthStr, 10);
   const day = parseInt(dayStr, 10);

   if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return null;
   }

   if (month < 1 || month > 12 || day < 1 || day > 31) {
      return null;
   }

   return new Date(year, month - 1, day);
}

/**
 * Formats a date-only string (YYYY-MM-DD) for display.
 *
 * @param dateString - Date string in YYYY-MM-DD format
 * @param options - Intl.DateTimeFormat options (defaults to short month, numeric day, numeric year)
 * @returns Formatted date string, or '-' if invalid
 *
 * @example
 * formatDateString("2026-01-10"); // "Jan 10, 2026"
 * formatDateString("2026-01-10", { month: 'long' }); // "January 10, 2026"
 */
export function formatDateString(
   dateString: string | null | undefined,
   options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
): string {
   const date = parseDateString(dateString);
   if (!date) {
      return '-';
   }
   return date.toLocaleDateString('en-US', options);
}

/**
 * Parses any date/datetime string safely.
 *
 * - For date-only strings (YYYY-MM-DD): parses as local time to avoid timezone shift
 * - For datetime strings (ISO 8601 with time/timezone): uses standard Date parsing
 *
 * @param dateString - Date or datetime string
 * @returns Date object, or null if invalid
 *
 * @example
 * parseAnyDateString("2026-01-10");           // Local time Jan 10
 * parseAnyDateString("2026-01-10T15:30:00Z"); // UTC datetime (standard parsing)
 */
export function parseAnyDateString(dateString: string | null | undefined): Date | null {
   if (!dateString) {
      return null;
   }

   // Check if it's a date-only string (YYYY-MM-DD format, exactly 10 chars)
   if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return parseDateString(dateString);
   }

   // For datetime strings, use standard parsing (timezone is explicit)
   const date = new Date(dateString);
   return isNaN(date.getTime()) ? null : date;
}

/**
 * Formats any date/datetime string for display.
 *
 * Handles both date-only (YYYY-MM-DD) and full datetime strings safely.
 *
 * @param dateString - Date or datetime string
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string, or '-' if invalid
 */
export function formatAnyDateString(
   dateString: string | null | undefined,
   options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
): string {
   const date = parseAnyDateString(dateString);
   if (!date) {
      return '-';
   }
   return date.toLocaleDateString('en-US', options);
}
